/* eslint-disable react/no-array-index-key */
import { useRef, SVGProps, ReactNode, useCallback } from 'react';

import { ControlledMenu, MenuItem, useClick } from '@szhsin/react-menu';
import type { ControlledMenuProps, MenuState } from '@szhsin/react-menu';
import cn from 'classnames';

import useLockedBody from 'hooks/useLockedBody';
import { ArrowDown } from 'styles/assets/icons';
import Check from 'styles/assets/icons/CheckIcon';
import MuiCheckBox from 'UIKit/MuiCheckBox';

import styles from './dropdown.module.scss';

export interface FilterDropDownProps {
  menuState: {
    state?: MenuState;
    endTransition: () => void;
  };
  toggleMenu: (isOpen?: boolean) => void;
  onCloseMenu?: () => void;
  label?: ReactNode;
  options: Option[];
  labelIcon?: SVGProps<SVGSVGElement> | ReactNode;
  selectionText?: string;
  closeOnSelect?: boolean;
  styleOverrideClasses?: Record<string, string>;
  showChevron?: boolean;
  children?: ReactNode;
  testId: string;
  titleTooltip?: string;
  autoPosition?: boolean;
  className?: string;
  btnClassName?: string;
  showActiveIcon?: boolean;
  overflow?: ControlledMenuProps['overflow'];
  showCheck?: boolean;
  disableHover?: boolean;
  menuProps?: Partial<ControlledMenuProps>;
  DropDownOpenIcon?: ReactNode;
  DropDownCloseIcon?: ReactNode;
  showCustomDropdownIcon?: boolean;
}

export interface Option {
  label?: ReactNode;
  checked?: boolean;
  id?: string | number;
  onClick?: () => void;
  checkbox?: boolean;
  optionType?: string;
  icon?: SVGProps<SVGSVGElement> | ReactNode;
  hide?: boolean;
  key?: string | number;
  dataTestId: string;
  className?: string;
}

const FilterDropDown = ({
  menuState,
  toggleMenu,
  onCloseMenu,
  label,
  options,
  selectionText,
  labelIcon,
  closeOnSelect,
  // Accepting various style overrides, this can be easily extended.
  styleOverrideClasses: soc = {},
  showChevron = true,
  children,
  testId,
  autoPosition,
  titleTooltip,
  overflow,
  className = '',
  btnClassName,
  showActiveIcon = true,
  showCheck = false, // Toggle check icon visibility
  disableHover = false,
  menuProps,
  DropDownOpenIcon,
  DropDownCloseIcon,
  showCustomDropdownIcon = false,
}: FilterDropDownProps) => {
  const anchorProps = useClick(menuState?.state, toggleMenu);
  const buttonRef = useRef(null);

  const isOpen = menuState?.state === 'open';

  useLockedBody(isOpen);

  const onMenuClose = useCallback(() => {
    if (typeof onCloseMenu === 'function') {
      onCloseMenu();
    } else {
      toggleMenu(false);
    }
  }, [onCloseMenu, toggleMenu]);

  return (
    <div
      className={cn(styles.parent, {
        [className]: className,
      })}
      title={titleTooltip}
    >
      {/* Menu Button */}
      <button
        type="button"
        data-testid={`${testId || ''}`}
        ref={buttonRef}
        // onMouseDown={() => toggleMenu()}
        className={cn(styles.button, {
          [soc.button]: soc.button,
          [styles.active]: isOpen,
          [styles.noGap]: !showChevron,
          [btnClassName as string]: btnClassName,
          [soc.activeButton]: isOpen,
        })}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...anchorProps}
      >
        {/* Label Icon */}
        {labelIcon && (
          <div
            className={cn(styles.iconContainer, {
              [soc.labelIcon]: !isOpen && showActiveIcon,
            })}
          >
            {labelIcon}
          </div>
        )}
        {/* Label Section */}
        <div className={cn(styles.labelSection, soc.labelSection)}>
          {/* Label */}
          {label && (
            <span className={cn(styles.label, soc.label, { [soc.labelActive]: isOpen })}>
              {label}
              {selectionText && selectionText.length !== 0 ? ':' : ''}
            </span>
          )}
          {/* Selection Text */}
          {selectionText && (
            <span className={cn(styles.label, styles.selectionText, soc.selectionText)}>
              {selectionText}
            </span>
          )}
          {/* Arrow Up/Down */}
          {showChevron && (
            <ArrowDown
              className={cn({
                [styles.arrowUp]: isOpen,
                [soc.arrowUp]: isOpen && soc?.arrowUp,
                [styles.arrowDown]: !isOpen,
                [soc.arrowDown]: !isOpen && soc?.arrowDown,
              })}
            />
          )}

          {showCustomDropdownIcon && (DropDownOpenIcon || DropDownCloseIcon) && (
            <>
              {isOpen && DropDownOpenIcon}
              {!isOpen && DropDownCloseIcon}
            </>
          )}
        </div>
      </button>

      {/* Dropdown */}
      <ControlledMenu
        anchorRef={buttonRef}
        onClose={onMenuClose}
        menuClassName={cn(styles.dropdown, soc.dropDown)}
        data-testid={`list-${testId}`}
        align={menuProps?.align}
        direction={menuProps?.direction}
        position={menuProps?.position ?? (autoPosition ? 'auto' : 'initial')}
        gap={menuProps?.gap ?? 8}
        shift={menuProps?.shift}
        overflow={menuProps?.overflow ?? overflow}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...menuState}
      >
        {options &&
          options.map((option, index) => {
            if (option.hide) return null;
            if (option.optionType === 'header') {
              return (
                <MenuItem
                  className={cn(
                    styles.optionLabel,
                    styles.headerOption,
                    soc.headerOption
                  )}
                  key={`${option.id}-${index}`}
                >
                  {option.label}
                </MenuItem>
              );
            }
            return (
              /* Dropdown Option */
              <MenuItem
                data-testid={`${testId}-${
                  option.dataTestId || option.dataTestId || option.label
                }-option`}
                className={cn(
                  {
                    [styles.dropdownOption]: !disableHover,
                  },
                  soc.dropdownOption,
                  option.className,
                  option.checked && [styles.active, soc.activeItem]
                )}
                key={`${option.id}-${index}`}
                onClick={e => {
                  if (option.onClick) option.onClick();
                  // eslint-disable-next-line no-param-reassign
                  e.keepOpen = !closeOnSelect;
                }}
              >
                <>
                  {/* Option Icon */}
                  {option.icon && (
                    <div className={styles.iconContainer}>{option.icon}</div>
                  )}
                  {option.checkbox && (
                    /* Option CheckBox */
                    <MuiCheckBox checked={option.checked} />
                  )}
                  {/* Option Label */}
                  {showCheck && option.checked && <Check />}
                  <div className={cn(styles.label, styles.optionLabel, soc.optionLabel)}>
                    {option.label}
                  </div>
                </>
              </MenuItem>
            );
          })}
        {children}
      </ControlledMenu>
    </div>
  );
};

export type ControlledFilterDropDownProps = Omit<
  FilterDropDownProps,
  // Omit state management props because we want to manage it in a wrapperComponent over FilterDropDown.
  'menuState' | 'toggleMenu' | 'closeMenu'
> & {
  // Optional Callbacks for when menu is toggled or closed.
  onToggleMenu?: () => void;
  onCloseMenu?: () => void;
};

export default FilterDropDown;
